import React from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Tab, Tabs} from "@material-ui/core";
import {Values} from "Tools/Values";
import {computed, observable} from "mobx";
import {IValuesStore} from "Stores/Values/types";
import {IManagementStore} from "Stores/Management/types";
import {IAuthStore} from "Stores/Auth/types";
import LocationDialogDataForm
    from "@src/Components/Pages/Main/Content/Locations/LocationDialog/LocationDialogDataForm/LocationDialogDataForm";
import getTimeZones from "Tools/Helpers/tzHelpers"
import LocationCity from "@material-ui/icons/LocationCity";
import DirectionsBoat from "@material-ui/icons/DirectionsBoat";
import LocalShipping from "@material-ui/icons/LocalShipping";
import PinDrop from "@material-ui/icons/PinDrop";
import {SvgIconProps} from "@material-ui/core/SvgIcon";
import {inject, observer} from "mobx-react";
import {TabPanel} from "@src/Shared/Components/TabPanel";
import {Position} from "@src/Interfaces/Entity/Position";
import _ from "lodash";
import {PositionsTab} from "@src/Components/Pages/Main/Content/Locations/LocationDialog/PositionsTab/PositionsTab";
import {Location, LocationPosition, MetaData} from "@src/Interfaces/Entity/Location";
import {MetaDataTab} from "@src/Components/Pages/Main/Content/Locations/LocationDialog/MetaDataTab/MetaDataTab";
import {DeployOnBareMetalTab} from "./DeployOnBareMetalTab/DeployOnBareMetalTab";

interface ILocationsDialogProps {
    initialValues?: Location;
    onConfirm: () => void;
    onClose: () => void;
    isOpened: boolean;
    values?: IValuesStore;
    management?: IManagementStore;
    auth?: IAuthStore;
}

export interface ItemType {
    title: string;
    type: number;
    icon: React.ComponentType<SvgIconProps>;
}

export interface IError {
    index: number,
    name?: string,
    value: string,
}

enum DialogTab {
    Location = 0,
    Positions = 1,
    Metadata = 2,
    Deploy = 3,
}

@inject("auth", "values", "management")
@observer
export class LocationDialog extends React.Component<ILocationsDialogProps> {
    private dataFormRef: any;
    private smallScreen = window.innerWidth < 1200;

    @observable tableHeight: number = 0;
    @observable selectedTab: DialogTab = this.smallScreen ? DialogTab.Location : DialogTab.Positions;
    @observable positions: Position[] = [];
    @observable positionsErrors: IError[] = [];
    @observable primaryLocationId: string = "";
    @observable metaData: MetaData[] = [];
    @observable metaDataErrors: IError[] = [];
    @observable isPositionsAndMetaDataValid: boolean = true;
    @observable location: Location = this.props.initialValues;
    @observable isConfirmBtnDisabled: boolean = false;

    async componentDidMount() {
        this.primaryLocationId = this.props.management.locations.find(item => item.primary).id || "";

        if (this.props.initialValues) {
            this.positions = _(this.props.initialValues.locationPositions || [])
                .filter(position =>
                    _.some(
                        this.props.management.activePositions,
                        x => x.id == position.positionId && position.state === this.props.values.states.Active
                    )
                )
                .map(position => this.props.management.activePositions.find(x => x.id == position.positionId))
                .orderBy(position => position.name)
                .value();
            if (this.props.initialValues.metaData) {
                this.metaData = this.props.initialValues.metaData;
            }
        }
    }

    @computed get isEdit(): boolean {
        return !!this.props.initialValues;
    }

    @computed get isSysAdmin(): boolean {
        return this.props.auth.user.roles.find(x => x == this.props.values.roles.Administrator) != null;
    }
    
    @computed get canDeployOnBareMetal(): boolean {
        //hide Deploy on bare metal tab until documentation link and script text provided
        return false;
        /*
        return this.isEdit
          && !this.location?.isVirtual 
          && this.location.state === 0 // active
          && !this.location.primary
          && this.isSysAdmin;
         */
    }

    toggleFormHeightChange(height: number) {
        const tableHeight = height - 177;
        if (this.tableHeight !== tableHeight) {
            this.tableHeight = tableHeight;
        }
    }

    getLocationTypes(): ItemType[] {
        return Object.entries(this.props.values.locationTypes).map((entry: [string, number]) => {
            const [titleKey, type] = entry;
            const title: string = Values.getLocalizationValue(titleKey);

            if (type === this.props.values.locationTypes.Office) {
                return {title, type, icon: LocationCity};
            } else if (type === this.props.values.locationTypes.Vessel) {
                return {title, type, icon: DirectionsBoat};
            } else if (type === this.props.values.locationTypes.Truck) {
                return {title, type, icon: LocalShipping};
            } else {
                return {title, type, icon: PinDrop};
            }
        });
    }

    validateFieldUniqueness(fieldName: string) {
        const locations = this.props.management.locations;
        return function (value: string) {
            if (!value) {
                return true;
            }
            const filteredLocation = locations.find(
                (location) => (location[fieldName] || "").toLowerCase() === value.toLowerCase()
            );
            if (!filteredLocation) {
                return true;
            }
            return this.parent.id !== "00000000-0000-0000-0000-000000000000" && filteredLocation.id === this.parent.id;
        };
    }

    primaryValidator(positionId: string) {
        const id = this.primaryLocationId
        return (value: boolean) => !(id === positionId && !value);
    }

    validateMetaDataNameUniqueness(value: string) {
        if (!value) {
            return Values.getLocalizationValue("fieldRequired");
        }
        const filteredMetaData = this.metaData.filter((item: MetaData) =>
            item.name.toLowerCase() === value.toLowerCase()
        );
        if (filteredMetaData.length === 1) {
            return "";
        }
        return Values.getLocalizationValue("nameAlreadyExists");
    }

    switchToTabWithErrors() {
        const positionTabNumber = this.smallScreen ? 1 : 0;
        const metadataTabNumber = this.smallScreen ? 2 : 1;

        this.selectedTab =  this.positionsErrors.length > 0
            ? positionTabNumber
            : metadataTabNumber;
    }

    validatePositionsAndMetaData() {
        const metaDataErrors: IError[] = [];
        const positionsErrors: IError[] = [];

        this.metaData?.forEach((item, i) => {
            const validationResult: IError = {index: i, name: "", value: ""};
            validationResult.name = this.validateMetaDataNameUniqueness(item.name);
            if (item.type === 3 && !item.value && !this.props.auth.user.location.primary) {
                validationResult.value = Values.getLocalizationValue("fieldRequired");
            }

            (validationResult.name || validationResult.value) && metaDataErrors.push(validationResult);
        });

        this.positions?.forEach((item, i) => {
            const validationResult: IError = {index: i, value: ""};
            !item.title && (validationResult.value = Values.getLocalizationValue("fieldRequired"));

            validationResult.value && positionsErrors.push(validationResult);
        })

        this.metaDataErrors = [...metaDataErrors];
        this.positionsErrors = [...positionsErrors];

        return positionsErrors.length === 0 && metaDataErrors.length === 0;
    }

    clearUnusedFormFields(location: Location) {
        const clearedLocation = {...location};

        if (location.type === 0) {
            clearedLocation.routeId = null;
        }

        if(location.type !== 1) {
            clearedLocation.imoNumber = null;
            clearedLocation.mmsiNumber = null;
        }

        if (location.type !== 2) {
            clearedLocation.licencePlate = null;
        }

        return clearedLocation;
    }

    async onFormSubmit(data: any) {
        if (this.isPositionsAndMetaDataValid) {
            this.isConfirmBtnDisabled = true;
            try {
                const updatedLocation: Location = {...data};
                updatedLocation.metaData = this.metaData;
                updatedLocation.locationPositions = this.positions.map(item => new LocationPosition(item.id, this.props.values.states.Active));
                const result = await this.props.management.saveLocation(this.clearUnusedFormFields(updatedLocation), !this.isEdit);
                if (result) {
                    if (updatedLocation.primary) {
                        const oldPrimaryLocation = this.props.management.locations.filter(item => (
                            item.id !== updatedLocation.id && item.primary
                        ));

                        if (oldPrimaryLocation.length > 0) {
                            oldPrimaryLocation[0].primary = false;
                            await this.props.management.saveLocation(oldPrimaryLocation[0]);
                        }
                    }

                    this.props.onClose();
                }
            } catch (error) {
                console.error(error);
            }
            this.isConfirmBtnDisabled = false;
        }
        // switch to person/metadata form with errors for small screen
        else if (this.smallScreen) {
            this.switchToTabWithErrors();
        }
    }

    async confirmButtonClick() {
        this.isPositionsAndMetaDataValid = this.validatePositionsAndMetaData();


        // on small screens we need to switch to form tab to create submit event and check if there are some errors in form
        // if form is valid, runs onFormSubmit(), where tab switches to next tab with errors
        if (this.smallScreen) {
            this.selectedTab = DialogTab.Location;
        }
        // on big screens we have separate column with form, so we can switch to tab with error even if form is not valid
        else if (!this.isPositionsAndMetaDataValid) {
            this.switchToTabWithErrors();
        }

        setTimeout(() => {
            this.dataFormRef.dispatchEvent(new Event("submit", {cancelable: true, bubbles: true}))
        }, 20);
    }

    onPositionsChange(positions: Position[]) {
        this.positions = [...positions];
    }

    onMetaDataChange(data: MetaData[]) {
        this.metaData = [...data];
    }
    
    renderLocationTabContent() {
        return (
          <>
              <LocationDialogDataForm
                initialValues={this.location}
                timeZones={getTimeZones()}
                locationTypes={this.getLocationTypes()}
                auth={this.props.auth}
                isNew={!this.isEdit}
                isSysAdmin={this.isSysAdmin}
                setRef={ref => this.dataFormRef = ref}
                onChange={(data) => this.location = {...data}}
                onSubmit={(data) => this.onFormSubmit(data)}
                uniquenessValidator={this.validateFieldUniqueness.bind(this)}
                primaryValidator={this.primaryValidator.bind(this)}
                toggleFormHeight={(height) => this.toggleFormHeightChange(height)}
              />
          </>
        );
    }

    render() {
        return (
            <Dialog
                maxWidth={"lg"}
                open={this.props.isOpened}
                onClose={this.props.onClose}
                data-test-id={"location-dialog"}
            >
                <DialogTitle id="person-dialog-title">
                    {this.isEdit ? Values.getLocalizationValue("editLocation") : Values.getLocalizationValue("addLocation")}
                </DialogTitle>

                <DialogContent
                    id={"locationDialog"}
                    style={{minWidth: `${this.smallScreen ? 700 : 1200}px`, width: "100%", paddingBottom: 32}}
                >
                    <Grid container spacing={5}>
                        {!this.smallScreen &&
                            <Grid item xs={4}>
                                {this.renderLocationTabContent()}
                            </Grid>
                        }

                        <Grid item xs={this.smallScreen ? 12 : 8}
                              style={{borderLeft: "1px solid #ececec", paddingTop: 0, paddingBottom: 0}}>
                            <Tabs
                                value={this.selectedTab}
                                onChange={(ev, val) => this.selectedTab = val}
                                indicatorColor={"primary"}
                            >
                                {this.smallScreen &&
                                    <Tab
                                      label={Values.getLocalizationValue("location")}
                                      data-test-id={"location-tab"}
                                      value={DialogTab.Location}
                                    />
                                }
                                <Tab
                                  label={Values.getLocalizationValue("positions")}
                                  data-test-id={"positions-tab"}
                                  value={DialogTab.Positions}
                                />
                                <Tab
                                  label={Values.getLocalizationValue("metadata")}
                                  data-test-id={"metadata-tab"}
                                  value={DialogTab.Metadata}
                                />
                                {this.canDeployOnBareMetal && (
                                  <Tab
                                    label={Values.getLocalizationValue("deployOnBareMetal")}
                                    data-test-id={"deploy-on-bare-metal-tab"}
                                    value={DialogTab.Deploy}
                                  />
                                )}
                            </Tabs>

                            {this.smallScreen &&
                                <TabPanel value={this.selectedTab} index={DialogTab.Location} data-test-id={"form-tab-panel"}>
                                    {this.renderLocationTabContent()}
                                </TabPanel>
                            }

                            <TabPanel value={this.selectedTab} index={DialogTab.Positions} data-test-id={"positions-tab-panel"}>
                                <PositionsTab
                                    tableHeight={this.tableHeight}
                                    location={this.location}
                                    positions={this.positions}
                                    errors={this.positionsErrors}
                                    onChange={(positions) => this.onPositionsChange(positions)}
                                />
                            </TabPanel>

                            <TabPanel value={this.selectedTab} index={DialogTab.Metadata} data-test-id={"metadata-tab-panel"}>
                                <MetaDataTab
                                    tableHeight={this.tableHeight}
                                    location={this.location}
                                    metaData={this.metaData}
                                    errors={this.metaDataErrors}
                                    onChange={(data) => this.onMetaDataChange(data)}
                                />
                            </TabPanel>

                            {this.canDeployOnBareMetal && (
                              <TabPanel index={DialogTab.Deploy} value={this.selectedTab} data-test-id="deploy-on-bare-metal">
                                  <DeployOnBareMetalTab
                                    location={this.location}
                                  />
                              </TabPanel>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions style={{padding: 16}}>
                    <Button variant={"outlined"} onClick={() => this.props.onClose()} color="default" data-test-id={"cancel"}>
                        {Values.getLocalizationValue("cancel")}
                    </Button>
                    <Button variant={"contained"} onClick={() => this.confirmButtonClick()}
                            disabled={this.isConfirmBtnDisabled} color="primary" data-test-id={"confirm"}>
                        {Values.getLocalizationValue("confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}
